import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import copy from "copy-to-clipboard";
import { Link, useLocation, useHistory } from "react-router-dom";
import ReactTooltip from 'react-tooltip';
import { toast } from "react-toastify";

import Table from "components/__new/common/Table";
import SearchBox from "components/__new/common/SearchBox";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import BankInterestGrey from "assets/images/bgs/bank-interest-grey.svg";
import { ReactComponent as IconCopy } from "assets/images/icons/project-icons/copy.svg";
import useLoginInfo from "hooks/useLoginInfo";
import ContentLoading from "components/__new/common/ContentLoading";
import sittingOnBench from "assets/images/bgs/SittingOnBench.png";
import CurrencyFormat from "utils/currencyFormat";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import processParams from "utils/processParams";
import useWallet from "hooks/useWallet";
import Maybe from "components/__new/common/Maybe";
import pluralize from "utils/pluralize";
import * as dashboardActions from "store/entities/dashboard/action";
import { renderStatusColor } from "utils/statusColor";
import { ButtonLoader, SelectInput } from "components/__new/common/form";
import { TransactionDetails } from "components/__new/payments/TransactionDetails";
import DateFormat, { TimeFormat } from "utils/dateFormat";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import { TableContent } from "components/__new/common/TableContent";
import { ReactComponent as IconArrowRight } from "assets/images/icons/project-icons/arrow-right.svg";
import { ReactComponent as IconEye } from "assets/images/icons/project-icons/eye.svg";
import { ReactComponent as IconEyeSlash } from "assets/images/icons/project-icons/eye-slash-dark.svg";
import { ReactComponent as IconCredit } from "assets/images/icons/project-icons/arrow-down-left.svg";
import { ReactComponent as IconDebit } from "assets/images/icons/project-icons/arrow-up-right.svg";
import { ReactComponent as YieldChart } from "assets/images/icons/project-icons/yield-chart.svg";
import { ReactComponent as ArrowUp } from "assets/images/icons/arrow-up.svg";
import { ReactComponent as ArrowDown } from "assets/images/icons/icon-arrow-down-red.svg";
import { ReactComponent as IconPlus } from "assets/images/icons/plus.svg";
import { ReactComponent as IconGreaterThan } from "assets/images/icons/greater-than.svg";
import { ReactComponent as IconInfo } from 'assets/images/icons/project-icons/information-new.svg'
import MonthlyBalanceGraph from 'components/__new/dashboardCharts/MonthlyBalanceGraph';
import * as walletAction from "store/entities/wallet/action";
import * as notificationAction from "store/entities/notification/action";
import CreateAccount from "components/__new/payments/CreateAccount";
import OptInOutYield from "./yield/OptInOutYield";
import MoveMoneyBetweenAccounts from "components/__new/payments/MoveMoneyBetweenAccounts";
import useUrlNavigation from 'hooks/useUrlNavigation';
import Spinner from "components/__new/common/Spinner";

export const Account = () => {
    const dispatch = useDispatch();
    const {userInfo, updateUserEarnYieldStatus} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const {urlQuery} = useUrlNavigation();
    const history = useHistory();
    const location = useLocation();
    const { walletLoading, createWallet } = useWallet();
    const { dashboardLoading: mainDashboardLoading, dashboard: mainDashboard, isFiltering } = useSelector((s) => s.entities.dashboard);
    const { totalWalletBalance, walletDataChat } = mainDashboard;
    const [isOpenCreateAccount, setIsOpenCreateAccount] = useState(false);
    const [isOpenYieldOptInOut, setIsOpenYieldOptInOut] = useState(false);
    const [isOpenMoveMoneyBetweenAccounts, setIsOpenMoveMoneyBetweenAccounts] = useState(false);
    const [dashboardListParams, setDashboardListParams] = useState({
        walletFilter: "",
    });
    const [selectedMonthFilter, setSelectedMonthFilter] = useState("last6months");
    const [filteringWalletData, setFilteringWalletData] = useState(false);
    const {
        accountDetailsLoading,
        dashboardLoading,
        dashboard,
        businessAccounts,
        yieldSettingsLoading,
        yieldSettings,
        yieldBalanceLoading,
        yieldBalance
    } = useSelector((s) => s.entities.wallet);
    const { data } = dashboard;    
    const { employerInfo } = useSelector((s) => s.auth.account);
    
    const getYieldSettings = () => {
        dispatch(walletAction.getYieldSettings());
        dispatch(walletAction.getYieldBalance());
    }

    const primaryAccount = businessAccounts?.data?.find(account => account?.isPrimaryAccount === true);

    const getDashboard = () => {
        if (dashboardLoading === false && (dashboard?.status === undefined || dashboard?.status === "failed")){
            dispatch(walletAction.getDashboardSilent(userInfo.token));
        }
    }
    
    const filterDashboardData = () => {
        let params = {...dashboardListParams};
        
        params = processParams(urlQueryParams, params, "walletFilter", "string", "", "wallet_filter");

        setDashboardListParams(params);
        dispatch(dashboardActions.filterDashboard(params));
    }
    
    const handleSelectedMonthFilterChange = (evt) => {
        const { value } = evt.target;
        history.push(urlQuery("walletFilter" + "=" + value));
        setSelectedMonthFilter(value);       
        setFilteringWalletData(true);
    }
    
    useEffect(() => {
        if (Object.entries(urlQueryParams).length > 0) {
            setSelectedMonthFilter(urlQueryParams?.walletFilter || "last6months");
        }
    }, [urlQueryParams])
    
    useEffect(() => {
        if (!isFiltering && filteringWalletData) {
            setFilteringWalletData(false);
        }
    }, [isFiltering])

    useEffect(() => {
        filterDashboardData();
    }, [location])
    
    useEffect(() => {
        getYieldSettings();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!employerInfo?.data?.employer) return;
        updateUserEarnYieldStatus(employerInfo?.data?.employer?.shouldEarnYield);
    }, [employerInfo]);

    useEffect(() => {
        getDashboard();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        dispatch(walletAction.getBusinessAccounts());
        // dispatch(dashboardActions.getDashboard());
        dispatch(notificationAction.getAllNotifications());
    }, [])

    return (
        <div>

            <DynamicHeadTag
                headerText="Wallet"
            />
            <CreateAccount
                isOpen={isOpenCreateAccount}
                setIsOpen={setIsOpenCreateAccount}
                createdAccounts={businessAccounts?.data?.length}
            />

            <OptInOutYield
                isOpen={isOpenYieldOptInOut}
                setIsOpen={setIsOpenYieldOptInOut}
                dashboardLoading={dashboardLoading}
                dashboard={dashboard}
                employer={userInfo?.employer}
            />

            <MoveMoneyBetweenAccounts
                isOpen={isOpenMoveMoneyBetweenAccounts}
                setIsOpen={setIsOpenMoveMoneyBetweenAccounts}
                accountDetailsLoading={accountDetailsLoading}
                accountDetails={primaryAccount}
            />

            <Maybe condition={dashboardLoading === true || dashboard?.status === "success"}>
                <div>
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className='bg-ep-white rounded-2xl my-5 p-5 w-full'>
                            <div className='flex flex-col lg:flex-row gap-6'>
                                <div className='lg:pr-6 lg:border-r lg:border-ep-gray-800'>
                                    <div className="flex gap-4 items-center">
                                        Total Balance
                                        <Maybe condition={businessAccounts?.data?.length > 0}>
                                            <ContentLoading
                                                loading={dashboardLoading}
                                                data={
                                                    <>
                                                        <span className="bg-ep-primary-600 bg-opacity-10 text-[#344054] rounded-2xl px-3">
                                                            {businessAccounts?.data?.length} {pluralize(businessAccounts?.data?.length, "account", "accounts")}
                                                        </span>
                                                    </>
                                                }
                                            />
                                        </Maybe>
                                    </div>
                                    <div className='font-bold text-3xl'>
                                        <div className="mt-2 flex items-center">
                                            <AccountBalance
                                                loading={mainDashboardLoading}
                                                balance={totalWalletBalance}
                                            />
                                        </div>
                                    </div>
                                    <ContentLoading
                                        loading={mainDashboardLoading}
                                        data={
                                            <>
                                                {
                                                    mainDashboard?.percentageIncrease?.status === "+" ? (
                                                        <div className='my-2 bg-ep-light-success rounded-xl px-5 py-1 text-ep-success inline-flex items-center'>
                                                            <span className='mr-2'><ArrowUp/></span>
                                                            {mainDashboard?.percentageIncrease?.status}{mainDashboard?.percentageIncrease?.percentage}% {mainDashboard?.percentageIncrease?.status === "+" ? "more than" : "less than"} last month
                                                        </div>
                                                    ) : (
                                                        <div className='my-2 bg-[#FEF3F2] rounded-xl px-5 py-1 text-[#B42318] inline-flex items-center'>
                                                            <span className='mr-2'><ArrowDown/></span>
                                                            {mainDashboard?.percentageIncrease?.status}{mainDashboard?.percentageIncrease?.percentage}% {mainDashboard?.percentageIncrease?.status === "+" ? "more than" : "less than"} last month
                                                        </div>
                                                    )
                                                }
                                            </>
                                        }
                                    />

                                    <div className=' flex flex-wrap sm:flex-nowrap'>
                                        <button type="button" onClick={() => setIsOpenCreateAccount(true)} className="btn btn-lg !px-5 mt-3 mr-3 xl:btn-block whitespace-nowrap text-white bg-ep-primary-600">
                                            <IconPlus className="mr-2" />
                                            Create New Account
                                        </button>
                                        <button onClick={() => setIsOpenMoveMoneyBetweenAccounts((prevState) => !prevState)} className="btn btn-lg text-[#193F52] !px-5 mt-3 xl:btn-block whitespace-nowrap bg-ep-primary-600 bg-opacity-10">
                                            {/* <IconSend className="mr-2" /> */}
                                            Move Money
                                        </button>
                                    </div>
                                </div>
                                <div className='w-full'>
                                    <div className="flex justify-end">
                                        <SelectInput onChange={handleSelectedMonthFilterChange} value={selectedMonthFilter}>
                                            <option value="" disabled>Select</option>
                                            <option value="last6months">Last 6 Months</option>
                                            <option value="last3months">Last 3 Months</option>
                                        </SelectInput>
                                    </div>
                                    <Maybe condition={walletDataChat?.length > 0 && businessAccounts?.data?.length > 0 && !filteringWalletData}>
                                        <MonthlyBalanceGraph
                                            // loading={mainDashboardLoading}
                                            chartData={walletDataChat}
                                        />
                                    </Maybe>
                                    <Maybe condition={filteringWalletData}>
                                        <Spinner spinnerTableInfoClassNameReplace="p-0" />
                                    </Maybe>
                                    <Maybe condition={walletDataChat?.length === 0}>
                                        <div className='text-center text-[#4A5564] border rounded-xl p-3'>
                                            <div className='my-2'>No Data Available Yet.</div>
                                            <div className='my-2'>Your data will appear here once you complete a transaction.</div>
                                        </div>
                                    </Maybe>
                                </div>
                            </div>
                        </div>
                        <Maybe condition={userInfo?.employer?.shouldEarnYield === true}>
                            <div className='bg-ep-white rounded-2xl my-5 p-6 lg:w-1/4'>
                                <div className="text-[#374151] mb-3">Yield earned this month</div>
                                <div className="mb-3 bg-ep-banner text-ep-secondary-600 rounded-full inline-block">
                                    <div className="flex items-center justify-evenly px-4 py-1 space-x-3">
                                        <div className="w-2 h-2 bg-ep-secondary rounded-full"></div>
                                        <div className="text-sm">
                                            <ContentLoading
                                                loading={yieldSettingsLoading}
                                                data={yieldSettings?.interestRate + "% per annum"}
                                            />
                                        </div>
                                        <div>
                                            <IconInfo className="text-[#BD7B16] cursor-pointer" data-tip data-for="yield-info"/>
                                            <ReactTooltip id="yield-info" place="bottom" effect="solid">
                                                Yield earned on all expense balances and is credited monthly to your primary account.
                                            </ReactTooltip>
                                        </div>
                                    </div>
                                </div>
                                <div className="font-bold text-2xl">
                                    <ContentLoading
                                        loading={yieldBalanceLoading}
                                        data={
                                            <>
                                                {CurrencyFormat(yieldBalance?.data?.balance / 100 || 0, true)}
                                            </>
                                        }
                                    />
                                </div>
                                <div className="mt-10">
                                    <Link to="/account/yield" className="btn btn-lg border rounded-xl font-medium text-sm border-ep-primary-600 text-ep-primary-600 !px-5 mt-3 xl:btn-block whitespace-nowrap bg-ep-white">
                                        <div className="flex items-center">
                                            <div className="mr-3">See Yield Report</div>
                                            <span><IconArrowRight/></span>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        </Maybe>
                        <Maybe condition={userInfo?.employer?.shouldEarnYield === false}>
                            <div className="bg-left-to-right-gradient-no-yield rounded-2xl my-5 p-6 lg:w-1/4 relative overflow-hidden">
                                <div className="absolute bottom-0 right-0 z-0">
                                    <img src={BankInterestGrey} alt="Bank Interest" className="object-contain" />
                                </div>
                                <div className="relative">
                                    <div className="flex items-center justify-between">
                                        <div className="font-medium text-xl flex flex-col gap-6">
                                            <div className="mb-3 text-[#18487F]">
                                                Earn Interest on your balance when you use <span className="font-semibold">Earnipay</span>
                                            </div>
                                            <div>
                                                <button type="button" className="btn btn-lg rounded-lg whitespace-nowrap text-white bg-ep-primary-600" onClick={() => setIsOpenYieldOptInOut(true)}>
                                                    Opt-In For Yield
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Maybe>
                    </div>
                </div>


                <AllAcounts />
            </Maybe>


            <Maybe condition={dashboardLoading === false && (dashboard?.status === undefined || dashboard?.status === "failed")}>
                <div className="min-h-[500px] flex justify-center items-center">
                    <div className="max-w-lg py-14">
                        <div className="h-48 flex">
                            <img src={sittingOnBench} alt="empty" className="max-w-full max-h-full m-auto object-contain" />
                        </div>

                        <div className="text-center mt-8">
                            <div className="font-semibold text-lg">
                                Sorry, you do not have a wallet yet.
                            </div>
                            <div className="text-fade mt-0.5">
                                Please click the button below to create your wallet in few seconds.
                            </div>
                            <div className="mt-8 flex justify-center">
                                <ButtonLoader type="button" loading={walletLoading} className="btn btn-ep-primary" onClick={createWallet}>
                                    Create Wallet
                                </ButtonLoader>
                            </div>
                        </div>
                    </div>
                </div>
            </Maybe>
        </div>
    )
}


const AccountBalance = (props) => {
    const {loading, balance} = props;
    const [isBalanceVisible, setIsBalanceVisible] = useState(true);

    return (
        <>
            <div className="text-3xl font-semibold mr-6">
                <Maybe condition={isBalanceVisible === true}>
                    <ContentLoading
                        loading={loading}
                        data={<>
                            {CurrencyFormat(balance / 100 || 0, true)}
                        </>}
                    />
                </Maybe>
                <Maybe condition={isBalanceVisible === false}>
                    ••••
                </Maybe>
            </div>
            <div onClick={() => setIsBalanceVisible(!isBalanceVisible)} className="w-7 h-7 rounded-lg cursor-pointer">
                <Maybe condition={isBalanceVisible === true}>
                    <IconEyeSlash className="w-full h-full object-contain svg-stroke-black" />
                </Maybe>
                <Maybe condition={isBalanceVisible === false}>
                    <IconEye className="w-full h-full object-contain svg-stroke-black" />
                </Maybe>
            </div>
        </>
    )
}

const YieldDashboard = (props) => {
    const { loading, dashboard } = props;
    const dispatch = useDispatch();
    const { yieldSettingsLoading, yieldSettings } = useSelector((s) => s.entities.wallet);
    
    const getYieldSettings = () => {
        dispatch(walletAction.getYieldSettings());
    }
    
    useEffect(() => {
        getYieldSettings();
        // eslint-disable-next-line
    }, [])
    
    return (
        <div className="h-full px-4 sm:px-6 py-2 sm:py-3 relative bg-yield">
            <div className="flex items-center">
                <div className="text-[#717171] mr-3">Yield</div>
                <div className="label h-6 text-sm text-[#BD7B16] bg-[#FEF5E7]">
                    <span className="text-2xl text-[#F49D1B] font-bold mr-2 position -top-px">•</span>
                    <ContentLoading
                        loading={yieldSettingsLoading}
                        data={yieldSettings?.interestRate + "% per month"}
                    />
                </div>
            </div>
            <div className="mt-2 text-2xl font-semibold">
                <ContentLoading
                    loading={loading}
                    data={CurrencyFormat(dashboard?.yieldBalance / 100) ?? "-"}
                />
            </div>
            <div className="mt-2 w-48 h-16">
                <YieldChart className="w-full relative -left-3" />
            </div>
            <div className="absolute bottom-4">
                <Link to="/payments/account/yield" className="btn btn-transparent-primary rounded-lg">
                    View Report
                    <IconArrowRight className="ml-2" />
                </Link>
            </div>
        </div>
    )
}

const RecentTransactions = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { transactionsLoading, transactions } = useSelector((s) => s.entities.wallet);


    const [isOpen, setIsOpen] = useState(false);
    const [transaction, setTransaction] = useState({});
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 10,
    });


    const getTransactions = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 10, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);        
        dispatch(walletAction.getTransactionsSilent(userInfo.token, params));
    }


    const viewDetails = (transaction) => {
        setTransaction(transaction);
        setIsOpen(true);
    }

    
    useEffect(() => {
        getTransactions();
        // eslint-disable-next-line
    }, [location]);


    return (
        <>
            <TransactionDetails
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                transaction={transaction}
            />
        
            <div className="mt-6 box !px-0 !pb-0">
                <div className="px-4 lg:flex">
                    <div className="w-full lg:w-1/2">
                        <div className="font-medium text-xl flex items-center">
                            <span className="mr-4">Recent Transactions</span>
                            <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                                <ContentLoading
                                    loading={transactionsLoading}
                                    data={<>{transactions?.data?.totalDocs ?? "-"} {pluralize(transactions?.data?.totalDocs, "transaction", "transactions")}</>}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-1/2 flex mt-2 lg:mt-0 lg:justify-end space-x-3">
                        <Link 
                            to={{ pathname: "/payments/transactions", state: {from: location }}} 
                            className="h-6 text-ep-primary-600 border-b-2 border-ep-primary-600">
                            View all
                        </Link>
                    </div>
                </div>

                <div className="mt-6">
                    <Table classTable="table-hover">
                        <Table.Head>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Previous Balance</th>
                            <th>Details</th>
                            <th>Amount</th>
                            <th>Current Balance</th>
                            <th>Status</th>
                        </Table.Head>
                        <Table.Body>
           
                            <TableContent
                                loading={transactionsLoading}
                                loadingText="Loading transactions"
                                data={transactions?.data}
                                colspan={7}
                                emptyUI={<>
                                    <div className="py-20 mx-auto text-center text-base">
                                        <div className="flex justify-center">
                                            <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                        </div>
                                        <div className="text-[#101828] font-medium">
                                            No Data Available Yet
                                        </div>
                                        <div className="mt-1 text-[#4A5564]">
                                            Once you start transacting, it would appear here
                                        </div>
                                    </div>
                                </>}
                            />

                            {transactionsLoading === false && transactions?.data?.docs?.map((transaction) =>
                                <tr key={transaction._id}>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        {DateFormat(transaction.createdAt)}
                                        &nbsp;at&nbsp;
                                        {TimeFormat(transaction.createdAt)}
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}} className="capitalize">
                                        <div className="flex items-center">
                                            <Maybe condition={transaction?.type === "credit"}>
                                                <div className="w-8 h-8 mr-2 flex bg-[#CFFFE3] rounded-full flex-shrink-0">
                                                    <IconCredit className="m-auto" />
                                                </div>
                                            </Maybe>
                                            <Maybe condition={transaction?.type === "debit"}>
                                                <div className="w-8 h-8 mr-2 flex bg-[#FEF2F2] rounded-full flex-shrink-0">
                                                    <IconDebit className="m-auto" />
                                                </div>
                                            </Maybe>
                                            <div>
                                                {transaction.type}
                                            </div>
                                        </div>
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        {CurrencyFormat(transaction.previousBalance / 100, true)}
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        <div className="capitalize">
                                            {transaction?.sourceType?.replaceAll("-", " ")}
                                        </div>
                                        <div className="max-w-[200px] text-fade" style={{whiteSpace: "normal"}}>
                                            {transaction.description}
                                        </div>
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        {CurrencyFormat(transaction.amount / 100, true)}
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        {CurrencyFormat(transaction.newBalance / 100, true)}
                                    </td>
                                    <td onClick={() => {viewDetails(transaction)}}>
                                        <div className={"label " + renderStatusColor(transaction.status)}>
                                            {transaction.status}
                                        </div>
                                    </td>
                                </tr>
                            )}

                        </Table.Body>
                    </Table>
                </div>
            </div>
        </>
    )
}

const AllAcounts = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const history = useHistory();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { businessAccountsLoading, businessAccounts } = useSelector((s) => s.entities.wallet);


    const [isOpen, setIsOpen] = useState(false);
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 10,
    });

    const viewAccountDetails = (walletId) => {
        history.push(`/account/all-accounts/${walletId}`);
    }

    const getAccounts = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 10, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);
        dispatch(walletAction.getBusinessAccounts(params));
    }

    const copyText = (accountNumber) => {
        let content = accountNumber;
        copy(content);
        toast.success(`${content} copied to clipboard`);
    }


    useEffect(() => {
        getAccounts();
        dispatch(notificationAction.getAllNotifications());
        // eslint-disable-next-line
    }, [location]);


    return (
        <>
            {/* <TransactionDetails
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                transaction={transaction}
            /> */}
        
            <div className="mt-2 box !px-0 !pb-0 rounded-xl">
                <div className="px-4 lg:flex">
                    <div className="w-full lg:w-1/2">
                        <div className="font-medium text-xl flex items-center">
                            <span className="mr-4">Accounts</span>
                            <div className="label h-6 text-sm font-normal text-[#BD7B16] bg-[#FFFAEB]">
                                <ContentLoading
                                    loading={businessAccountsLoading}
                                    data={<> You can still create {10 - businessAccounts?.data?.length} more {pluralize(businessAccounts?.data?.length, "account", "accounts")}</>}
                                />
                            </div>
                        </div>
                        <div className="max-w-sm flex-grow sm:mr-6 mt-5">
                            <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                        </div>
                    </div>
                    {/* <div className="w-full lg:w-1/2 flex mt-2 lg:mt-0 lg:justify-end space-x-3">
                        <div>
                            <SelectInput>
                                <option value="" disabled>Select</option>
                                <option value="byRecent">Sort By Most Recent</option>
                                <option value="byDate">Sort By Date</option>
                            </SelectInput>
                        </div>
                    </div> */}
                </div>

                <div className="mt-6">
                    <Table classTable="table-hover">
                        <Table.Head>
                            <th>Account Name</th>
                            <th>Account Number</th>
                            <th>Bank Name</th>
                            <th>Balance</th>
                            <th>Account Type</th>
                            <th>Status</th>
                            <th></th>
                        </Table.Head>
                        <Table.Body>

                            <TableContent
                                loading={businessAccountsLoading}
                                loadingText="Loading accounts"
                                data={businessAccounts?.data}
                                colspan={7}
                                emptyUI={<>
                                    <div className="py-20 mx-auto text-center text-base">
                                        <div className="flex justify-center">
                                            <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                        </div>
                                        <div className="text-[#101828] font-medium">
                                            No Data Available Yet
                                        </div>
                                        <div className="mt-1 text-[#4A5564]">
                                            Once you create accounts, they would appear here
                                        </div>
                                    </div>
                                </>}
                            />

                            {businessAccountsLoading === false && businessAccounts?.data?.map((account) =>
                                    <tr onClick={() => viewAccountDetails(account?.walletId)} key={account?._id}>
                                        <td>
                                            {account?.accountName ?
                                                <>
                                                    {account?.accountName}
                                                    {account?.isPrimaryAccount && <span className="ml-3 text-ep-primary-600 bg-ep-primary-600 bg-opacity-10 rounded-full px-3 py-1">Primary</span>}
                                                </> : "N/A"
                                            }
                                        </td>
                                        <td className="flex">
                                            {account?.accountNumber ?
                                                <>
                                                    {account?.accountNumber}
                                                    <IconCopy onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyText(account?.accountNumber)
                                                    }} className="ml-1 cursor-pointer" />
                                                </> : "N/A"
                                            }
                                        </td>
                                        <td className="capitalize">
                                            {account?.accountNumber ? account?.bankName : "N/A"}
                                        </td>
                                        <td>
                                            {CurrencyFormat(account?.balance / 100, true)}
                                        </td>
                                        <td className="capitalize">
                                            {account?.companyAccountType || account?.walletType}
                                        </td>
                                        <td className="capitalize">
                                            <div className={"label " + renderStatusColor(account?.accountStatus)}>
                                                {account?.accountStatus}
                                            </div>
                                        </td>
                                        <td><IconGreaterThan /></td>
                                    </tr>
                            )}
                        </Table.Body>
                    </Table>
                </div>
            </div>
        </>
    )
}

export default Account;